import React, {Suspense} from 'react'

import type {
    // DOMConversionMap,
    // DOMConversionOutput,
    // DOMExportOutput,
    EditorConfig,
    LexicalNode,
    NodeKey,
    SerializedLexicalNode,
    Spread,
} from 'lexical'
import {$applyNodeReplacement, DecoratorNode} from 'lexical'
import {AlignType} from "../../../../rich-editor/plugins/resizeable/align-selector/align-selector"
import Attachment from "../../../../../model2/attachment/attachment"

const DEFAULT_WIDTH = '40%'

const AttachmentComponent = React.lazy(
    // @ts-ignore
    () => import('./component'),
)

export interface AttachmentPayload {
    attachment  : Attachment
    height?     : string | number
    width?      : string | number
    key?        : NodeKey
    alignment?  : AlignType
}

/*function convertImageElement(domNode: Node): null | DOMConversionOutput {
    if (domNode instanceof HTMLImageElement) {
        const {alt: altText, src, width, height} = domNode;
        const node = $createAttachmentNode({altText, height, src, width});
        return {node};
    }
    return null;
}*/

export type SerializedAttachmentNode = Spread<
    {
        attachment      : any
        width?          : string | number
        height?         : string | number
        alignment       : AlignType
    },
    SerializedLexicalNode
>

// const debug = 'AttachmentNode'
const debug = false

export class AttachmentNode extends DecoratorNode<JSX.Element> {
    __attachment    : Attachment
    __width         : string | number
    __height        : string | number
    __alignment     : AlignType

    static getType(): string {
        return 'attachment'
    }

    static clone(node: AttachmentNode): AttachmentNode {
        return new AttachmentNode(
            node.__attachment,
            node.__width,
            node.__height,
            node.__key,
            node.__alignment
        )
    }

    static importJSON(serializedNode: SerializedAttachmentNode): AttachmentNode {
        const {attachment, width, height, alignment} = serializedNode
        return $createAttachmentNode({
            attachment: new Attachment(attachment),
            width,
            height,
            alignment,
        })
    }

    // copy/paste?
    /*exportDOM(): DOMExportOutput {
        const element = document.createElement('img');
        element.setAttribute('src', this.__src);
        element.setAttribute('alt', this.__altText);
        element.setAttribute('width', this.__width.toString());
        element.setAttribute('height', this.__height.toString());
        return {element};
    }

    static importDOM(): DOMConversionMap | null {
        return {
            img: (node: Node) => ({
                conversion: convertImageElement,
                priority: 0,
            }),
        };
    }*/

    constructor(
        attachment  : Attachment,
        width?      : string | number,
        height?     : string | number,
        key?        : NodeKey,
        alignment?  : AlignType,
    ) {
        super(key)
        this.__attachment   = attachment
        this.__width        = width || DEFAULT_WIDTH
        this.__height       = height || 'auto'
        this.__alignment    = alignment || AlignType.LEFT
    }

    exportJSON(): SerializedAttachmentNode {
        return {
            attachment  : this.__attachment.toJSON(),
            height      : this.__height,
            width       : this.__width,
            alignment   : this.getAlignment(),
            type        : AttachmentNode.getType(),
            version     : 1,
        };
    }

    setWidthAndHeight(
        width: string | number,
        height: string | number,
    ): void {
        const writable = this.getWritable();
        writable.__width = width;
        writable.__height = height;
    }

    // View

    createDOM(config: EditorConfig): HTMLElement {
        const span  = document.createElement('span')
        const theme = config.theme
        const className = theme.attachment
        if (className !== undefined) {
            span.className = className
        }
        return span
    }

    updateDOM(): false {
        return false
    }

    setAlignment(value: AlignType) {
        // getWritable() creates a clone of the node
        // if needed, to ensure we don't try and mutate
        // a stale version of this node.
        const self = this.getWritable()
        self.__alignment = value
    }

    getAlignment():AlignType {
        // getLatest() ensures we are getting the most
        // up-to-date value from the EditorState.
        const self = this.getLatest()
        return self.__alignment
    }

    decorate(): JSX.Element {
        return (
            <Suspense fallback={null}>
                <AttachmentComponent attachment = {this.__attachment}
                                     width      = {this.__width}
                                     height     = {this.__height}
                                     alignment  = {this.__alignment}
                                     nodeKey    = {this.getKey()}/>
            </Suspense>
        )
    }
}

export function $createAttachmentNode({attachment, width, height, key, alignment}: AttachmentPayload): AttachmentNode {
    return $applyNodeReplacement(new AttachmentNode(attachment, width, height, key, alignment))
}

export function $isAttachmentNode(node: LexicalNode | null | undefined): node is AttachmentNode {
    return node instanceof AttachmentNode
}
